/**
 * UpgradeModal — rendered once in App.tsx, driven by usePlanGateStore.
 *
 * Shows a contextual upgrade prompt when the backend returns a 402
 * with structured PlanLimitDetails. Embeds the plan picker inline
 * so the user can compare and click through to /subscribe?plan=<tier>.
 */
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePlanGateStore, type PlanLimitType } from "@/hooks/usePlanGate.ts";
import { billingApi } from "@/api/billing.ts";
import { Btn, Icon } from "@/components/v2/primitives.tsx";
import type { PlanLimits } from "@vms/tubeforge-shared";
import { shownMaxLength } from "@/pages/billing/plan-price.ts";   // (session 10)

/* ── Copy per limit type ─────────────────────────────────────────── */

const LIMIT_COPY: Record<
  PlanLimitType,
  { icon: string; title: string; body: (plan: string | null, limit?: number) => string }
> = {
  videos_per_month: {
    icon: "video",
    title: "Video limit reached",
    body: (plan, limit) =>
      `You've hit the ${limit ?? ""} video${limit === 1 ? "" : "s"}/month cap on ${planName(plan)}.`,
  },
  regens_per_segment: {
    icon: "refresh",
    title: "Regen limit reached",
    body: (plan) =>
      `You've used all image regenerations for this segment on ${planName(plan)}.`,
  },
  connected_channels: {
    icon: "youtube",
    title: "Channel limit reached",
    body: (plan, limit) =>
      `${planName(plan)} supports ${limit ?? 0} connected channel${limit === 1 ? "" : "s"}.`,
  },
  active_series: {
    icon: "layers",
    title: "Series limit reached",
    body: (plan, limit) =>
      `You can run ${limit ?? 0} active series on ${planName(plan)}.`,
  },
  team_seats: {
    icon: "shield",
    title: "Team seats limit",
    body: (plan) =>
      `${planName(plan)} doesn't include team seats.`,
  },
  youtube_publish: {
    icon: "youtube",
    title: "Publishing not available",
    body: (plan) =>
      `YouTube publishing isn't available on ${planName(plan)}.`,
  },
  scheduled_publish: {
    icon: "clock",
    title: "Scheduling not available",
    body: (plan) =>
      `Scheduled publishing isn't available on ${planName(plan)}.`,
  },
  no_active_plan: {
    icon: "creditCard",
    title: "No active plan",
    body: () =>
      "You need an active subscription to use this feature.",
  },
  credits: {
    icon: "creditCard",
    title: "Out of credits",
    body: (plan) =>
      `You've used all your credits on ${planName(plan)}.`,
  },
};

function planName(plan: string | null): string {
  if (!plan) return "your current plan";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

/* ── Feature bullets per limit type ──────────────────────────────── */

function planFeatures(p: PlanLimits): string[] {
  const len = shownMaxLength(p.maxVideoLengthSeconds);   // (session 10, D2) no 30-minute claim
  return [
    `${p.videosPerMonth} videos/month`,
    len ? `Up to ${len} videos` : null,
    p.imageModel.replace("flux-", "Flux ").replace(/\b\w/g, (c) => c.toUpperCase()),
    p.maxConnectedChannels > 0
      ? `${p.maxConnectedChannels} channel${p.maxConnectedChannels === 1 ? "" : "s"}`
      : null,
    p.allowYoutubePublish ? "Direct YouTube publish" : null,
    p.teamSeats > 1 ? `${p.teamSeats} team seats` : null,
  ].filter(Boolean) as string[];
}

/* ── Component ───────────────────────────────────────────────────── */

export function UpgradeModal() {
  const { open, details, close } = usePlanGateStore();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const { data: plansData, isError: plansUnavailable } = useQuery({
    queryKey: ["billing", "plans"],
    queryFn: () => billingApi.plans(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const handler = () => close();
    d.addEventListener("close", handler);
    return () => d.removeEventListener("close", handler);
  }, [close]);

  if (!details) return null;

  const copy = LIMIT_COPY[details.limit] ?? LIMIT_COPY.no_active_plan;
  const plans = plansData?.plans ?? [];
  const recommended = details.requiredPlan;

  return (
    <dialog
      ref={dialogRef}
      className="backdrop:bg-black/40"
      onClick={(e) => { if (e.target === dialogRef.current) close(); }}
      style={{
        maxWidth: 720,
        width: "calc(100% - 32px)",
        padding: 0,
        border: "none",
        borderRadius: 16,
        background: "var(--tf-bg)",
        color: "var(--tf-fg)",
        boxShadow: "0 24px 48px -12px rgba(0,0,0,0.25)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div style={{ padding: "24px 24px 0", textAlign: "center" }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: "var(--tf-accent-soft)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 12,
          }}
        >
          <Icon name={copy.icon} size={22} color="var(--tf-accent)" />
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 6px" }}>
          {copy.title}
        </h2>
        <p style={{ fontSize: 14, color: "var(--tf-fg-muted)", margin: 0, lineHeight: 1.5 }}>
          {copy.body(details.currentPlan, details.currentLimit)}
          {" "}Upgrade to continue.
        </p>
      </div>

      {/* Plan cards */}
      <div style={{ padding: "20px 24px 24px" }}>
        {/* (audit) prices unavailable (the backend's 503): said, not an endless skeleton */}
        {plansUnavailable && plans.length === 0 ? (
          <p style={{ fontSize: 14, color: "var(--tf-fg-muted)", margin: 0 }}>Prices can't be shown right now. Please try again shortly.</p>
        ) : plans.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {plans.map((p) => {
              const isRec = p.tier === recommended;
              return (
                <div
                  key={p.tier}
                  style={{
                    border: isRec
                      ? "2px solid var(--tf-accent)"
                      : "1px solid var(--tf-rule)",
                    borderRadius: 12,
                    padding: "16px 14px",
                    background: isRec ? "var(--tf-accent-soft)" : "var(--tf-surface)",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  {isRec && (
                    <div style={{
                      position: "absolute",
                      top: -1,
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      background: "var(--tf-accent)",
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "2px 10px",
                      borderRadius: 20,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}>
                      Recommended
                    </div>
                  )}

                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>
                    {p.displayName}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 24, fontWeight: 800 }}>${p.priceUsd}</span>
                    <span style={{ fontSize: 13, color: "var(--tf-fg-muted)" }}>/mo</span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, marginBottom: 14 }}>
                    {planFeatures(p).map((f) => (
                      <div key={f} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                        <Icon name="check" size={13} color="var(--tf-success)" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>

                  <Btn
                    variant={isRec ? "primary" : "subtle"}
                    size="sm"
                    fullWidth
                    onClick={() => {
                      close();
                      window.location.href = `/subscribe?plan=${p.tier}`;
                    }}
                  >
                    {isRec ? "Get started" : "Select"}
                  </Btn>
                </div>
              );
            })}
          </div>
        ) : (
          /* Loading skeleton while plans fetch */
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  border: "1px solid var(--tf-rule)",
                  borderRadius: 12,
                  padding: "16px 14px",
                  background: "var(--tf-surface)",
                  height: 200,
                }}
              />
            ))}
          </div>
        )}

        {/* Dismiss */}
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <Btn variant="ghost" size="sm" onClick={close}>Not now</Btn>
        </div>
      </div>

      {/* Close X */}
      <button
        type="button"
        onClick={close}
        aria-label="Close"
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          background: "none",
          border: "none",
          padding: 4,
          cursor: "pointer",
          borderRadius: 6,
          color: "var(--tf-fg-muted)",
          display: "flex",
        }}
      >
        <Icon name="x" size={16} />
      </button>
    </dialog>
  );
}
